import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBillingStatus, updateAutoTopUp } from "@/lib/api/billing";
import { useAuth } from "@/lib/contexts/AuthContext";
import type { BillingStatus } from "@/lib/types/billing";

// Switching it on can buy a block right away (server side, async), so the
// balance and the monthly count are re-read once that charge has had time.
const REFETCH_AFTER_SAVE_MS = 5_000;

/**
 * Agents plan auto top-up. `autoTopUp` is null on every other plan, or when
 * the backend has no top-up price configured, so callers render nothing.
 */
export function useAutoTopUp() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["billing-status", token] as const;

  const { data: status } = useQuery({
    queryKey,
    queryFn: () => getBillingStatus(token as string),
    enabled: !!token,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (settings: { enabled: boolean; maxPerPeriod?: number }) =>
      updateAutoTopUp(token as string, settings),
    onSuccess: (autoTopUp) => {
      queryClient.setQueryData<BillingStatus>(queryKey, (prev) =>
        prev ? { ...prev, autoTopUp } : prev,
      );
      window.setTimeout(
        () => void queryClient.invalidateQueries({ queryKey }),
        REFETCH_AFTER_SAVE_MS,
      );
    },
  });

  const autoTopUp =
    status?.planKind === "agents" && status.autoTopUp?.available ? status.autoTopUp : null;

  const topUps =
    status?.planKind === "agents" && status.topUps?.available && status.topUps.eligible ? status.topUps : null;

  return {
    autoTopUp,
    topUps,
    dataPoints: status?.dataPoints ?? null,
    refresh: () => queryClient.invalidateQueries({ queryKey }),
    save: mutation.mutateAsync,
    saving: mutation.isPending,
  };
}
